/**
 * LLM Model Service — calls hub proxy for /llm/* model endpoints.
 * Mirrors the shape of llm-provider.service.ts.
 */
import { hubCall } from "@/lib/api/hub";
import type { LLMModel, CreateModelInput, UpdateModelInput, OrgDefault } from "@/types/llm-model";
import type { ApiResponse, PaginatedResponse } from "@/types/api.types";

export const llmModelService = {
  /** List all models for a specific provider (GET /providers/:id/models). */
  async listByProvider(providerId: string): Promise<PaginatedResponse<LLMModel>> {
    return hubCall<PaginatedResponse<LLMModel>>(`/llm/providers/${providerId}/models`);
  },

  /** List system catalog models (GET /models/system). */
  async listSystem(): Promise<PaginatedResponse<LLMModel>> {
    return hubCall<PaginatedResponse<LLMModel>>("/llm/models/system");
  },

  /** Create a model under a provider (POST /providers/:id/models). */
  async create(providerId: string, payload: CreateModelInput): Promise<ApiResponse<LLMModel>> {
    return hubCall<ApiResponse<LLMModel>>(`/llm/providers/${providerId}/models`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  /** Update a model (PATCH /models/:id — partial update). */
  async update(id: string, payload: UpdateModelInput): Promise<ApiResponse<LLMModel>> {
    return hubCall<ApiResponse<LLMModel>>(`/llm/models/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },

  /** Delete a model (DELETE /models/:id). */
  async delete(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return hubCall<ApiResponse<{ deleted: boolean }>>(`/llm/models/${id}`, {
      method: "DELETE",
    });
  },

  /** Get org default for a use case (GET /defaults/:useCase). Returns 404 when not set. */
  async getOrgDefault(useCase: string): Promise<ApiResponse<OrgDefault>> {
    return hubCall<ApiResponse<OrgDefault>>(`/llm/defaults/${useCase}`);
  },

  /** Set org default for a use case (PUT /defaults/:useCase). */
  async setOrgDefault(
    useCase: string,
    providerId: string,
    modelId: string,
  ): Promise<ApiResponse<{ ok: boolean }>> {
    return hubCall<ApiResponse<{ ok: boolean }>>(`/llm/defaults/${useCase}`, {
      method: "PUT",
      body: JSON.stringify({ providerId: providerId, modelId: modelId }),
    });
  },
};
